import request from '../../../../core/wrappers/axios-wrapper';
import { ApiURL } from '../../../../types/constants/apiUrls/api-urls';
import { RequestType } from '../../../../types/enums/request-enums';

export type BatchProcessTrackingFrame = {
    rawImageId: number;
    photoTypeCode: string;
    photoTypeDisplayName: string;
    sortOrder: number;
    status: string;
    statusNameTr: string;
    filePath: string;
    processedFilePath: string;
    llmProcessedFilePath: string;
    llmThumbnail?: string;
    lastUpdatedAt: string;
};

export type BatchProcessTrackingItem = {
    barcode: string;
    specialCode: string;
    colorCode: string;
    colorName: string;
    productName: string;
    productClassification: string;
    assignee: string;
    createdBy: string;
    aggregateStatus: string;
    aggregateStatusNameTr: string;
    lastUpdatedAt: string;
    externalBatchId: string;
    frames: BatchProcessTrackingFrame[];
};

export type BatchProcessTrackingListResponse = {
    items: BatchProcessTrackingItem[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
};

export type BatchProcessTrackingMetrics = {
    preProcessingCount: number;
    aiProcessingCount: number;
    completedTodayCount: number;
    manualRequiredCount: number;
};

export type BatchProcessTrackingHistoryEntry = {
    rawImageId: number;
    photoTypeCode: string;
    oldStatus: string;
    oldStatusNameTr: string;
    newStatus: string;
    newStatusNameTr: string;
    createdBy: string;
    createDate: string;
};

export type BatchProcessTrackingBatchJob = {
    externalBatchId: string;
    status: string;
    retryCount: number;
    requestDate: string;
    completedDate: string;
    errorMessage: string;
};

export type BatchProcessTrackingDetail = {
    item: BatchProcessTrackingItem | null;
    history: BatchProcessTrackingHistoryEntry[];
    batchJobs: BatchProcessTrackingBatchJob[];
};

export type BatchProcessTrackingFilterParams = {
    barcode?: string;
    status?: string;
    createdBy?: string;
};

export type BatchProcessTrackingListParams = BatchProcessTrackingFilterParams & {
    pageNumber?: number;
    pageSize?: number;
};

type Raw = Record<string, unknown>;

const pick = (raw: Raw, camel: string, pascal: string) => raw[camel] ?? raw[pascal];
const str = (raw: Raw, camel: string, pascal: string) => String(pick(raw, camel, pascal) ?? '');
const num = (raw: Raw, camel: string, pascal: string) => Number(pick(raw, camel, pascal) ?? 0);
const list = (raw: Raw, camel: string, pascal: string) => (pick(raw, camel, pascal) ?? []) as Raw[];

const mapFrame = (raw: Raw): BatchProcessTrackingFrame => ({
    rawImageId: num(raw, 'rawImageId', 'RawImageId'),
    photoTypeCode: str(raw, 'photoTypeCode', 'PhotoTypeCode'),
    photoTypeDisplayName: str(raw, 'photoTypeDisplayName', 'PhotoTypeDisplayName'),
    sortOrder: num(raw, 'sortOrder', 'SortOrder'),
    status: str(raw, 'status', 'Status'),
    statusNameTr: str(raw, 'statusNameTr', 'StatusNameTr'),
    filePath: str(raw, 'filePath', 'FilePath'),
    processedFilePath: str(raw, 'processedFilePath', 'ProcessedFilePath'),
    llmProcessedFilePath: str(raw, 'llmProcessedFilePath', 'LlmProcessedFilePath'),
    llmThumbnail: str(raw, 'llmThumbnail', 'LlmThumbnail'),
    lastUpdatedAt: str(raw, 'lastUpdatedAt', 'LastUpdatedAt'),
});

const mapItem = (raw: Raw): BatchProcessTrackingItem => ({
    barcode: str(raw, 'barcode', 'Barcode'),
    specialCode: str(raw, 'specialCode', 'SpecialCode'),
    colorCode: str(raw, 'colorCode', 'ColorCode'),
    colorName: str(raw, 'colorName', 'ColorName'),
    productName: str(raw, 'productName', 'ProductName'),
    productClassification: str(raw, 'productClassification', 'ProductClassification'),
    assignee: str(raw, 'assignee', 'Assignee'),
    createdBy: str(raw, 'createdBy', 'CreatedBy'),
    aggregateStatus: str(raw, 'aggregateStatus', 'AggregateStatus'),
    aggregateStatusNameTr: str(raw, 'aggregateStatusNameTr', 'AggregateStatusNameTr'),
    lastUpdatedAt: str(raw, 'lastUpdatedAt', 'LastUpdatedAt'),
    externalBatchId: str(raw, 'externalBatchId', 'ExternalBatchId'),
    frames: list(raw, 'frames', 'Frames').map(mapFrame),
});

const GetItems = async (params: BatchProcessTrackingListParams): Promise<BatchProcessTrackingListResponse> => {
    const raw = (await request({
        url: ApiURL.Query.StudioQueryURLs.BatchProcessTrackingQueryURL.GetItems,
        method: RequestType.GET,
        params,
    })) as Raw;

    return {
        items: list(raw, 'items', 'Items').map(mapItem),
        totalCount: num(raw, 'totalCount', 'TotalCount'),
        pageNumber: Number(pick(raw, 'pageNumber', 'PageNumber') ?? 1),
        pageSize: Number(pick(raw, 'pageSize', 'PageSize') ?? 20),
    };
};

const GetMetrics = async (params: BatchProcessTrackingFilterParams): Promise<BatchProcessTrackingMetrics> => {
    const raw = (await request({
        url: ApiURL.Query.StudioQueryURLs.BatchProcessTrackingQueryURL.GetMetrics,
        method: RequestType.GET,
        params: { barcode: params.barcode, createdBy: params.createdBy },
    })) as Raw;

    return {
        preProcessingCount: num(raw, 'preProcessingCount', 'PreProcessingCount'),
        aiProcessingCount: num(raw, 'aiProcessingCount', 'AiProcessingCount'),
        completedTodayCount: num(raw, 'completedTodayCount', 'CompletedTodayCount'),
        manualRequiredCount: num(raw, 'manualRequiredCount', 'ManualRequiredCount'),
    };
};

const mapHistoryEntry = (entry: Raw): BatchProcessTrackingHistoryEntry => ({
    rawImageId: num(entry, 'rawImageId', 'RawImageId'),
    photoTypeCode: str(entry, 'photoTypeCode', 'PhotoTypeCode'),
    oldStatus: str(entry, 'oldStatus', 'OldStatus'),
    oldStatusNameTr: str(entry, 'oldStatusNameTr', 'OldStatusNameTr'),
    newStatus: str(entry, 'newStatus', 'NewStatus'),
    newStatusNameTr: str(entry, 'newStatusNameTr', 'NewStatusNameTr'),
    createdBy: str(entry, 'createdBy', 'CreatedBy'),
    createDate: str(entry, 'createDate', 'CreateDate'),
});

const GetDetail = async (barcode: string): Promise<BatchProcessTrackingDetail> => {
    const raw = (await request({
        url: ApiURL.Query.StudioQueryURLs.BatchProcessTrackingQueryURL.GetDetail,
        method: RequestType.GET,
        params: { barcode },
    })) as Raw;

    const item = pick(raw, 'item', 'Item') as Raw | null | undefined;

    return {
        item: item ? mapItem(item) : null,
        history: [],
        batchJobs: list(raw, 'batchJobs', 'BatchJobs').map((job) => ({
            externalBatchId: str(job, 'externalBatchId', 'ExternalBatchId'),
            status: str(job, 'status', 'Status'),
            retryCount: num(job, 'retryCount', 'RetryCount'),
            requestDate: str(job, 'requestDate', 'RequestDate'),
            completedDate: str(job, 'completedDate', 'CompletedDate'),
            errorMessage: str(job, 'errorMessage', 'ErrorMessage'),
        })),
    };
};

const GetHistory = async (barcode: string): Promise<BatchProcessTrackingHistoryEntry[]> => {
    const raw = (await request({
        url: ApiURL.Query.StudioQueryURLs.BatchProcessTrackingQueryURL.GetHistory,
        method: RequestType.GET,
        params: { barcode },
    })) as Raw;

    return list(raw, 'items', 'Items').map(mapHistoryEntry);
};

const GetCreatedByOptions = async (): Promise<string[]> => {
    const raw = (await request({
        url: ApiURL.Query.StudioQueryURLs.BatchProcessTrackingQueryURL.GetCreatedByOptions,
        method: RequestType.GET,
    })) as Raw;

    return ((pick(raw, 'items', 'Items') ?? []) as unknown[]).map((entry) => String(entry));
};

export type BatchProcessTrackingStatusOption = {
    statusName: string;
    statusDescription: string;
};

const GetStatusOptions = async (): Promise<BatchProcessTrackingStatusOption[]> => {
    const raw = (await request({
        url: ApiURL.Query.StudioQueryURLs.BatchProcessTrackingQueryURL.GetStatusOptions,
        method: RequestType.GET,
    })) as Raw;

    return list(raw, 'items', 'Items').map((entry) => ({
        statusName: str(entry, 'statusName', 'StatusName'),
        statusDescription: str(entry, 'statusDescription', 'StatusDescription'),
    }));
};

export type BarcodeProductionSummary = {
    barcode: string;
    hasPreviousProduction: boolean;
    /** Barkodun üretimi henüz tamamlanmamış, hattaki kayıtları var. */
    hasInProgressProcess: boolean;
    /** İşlemde olan kare kodları (örn. FrontShot). Yoksa / boşsa kare bazlı blok uygulanmaz. */
    inProgressPhotoTypeCodes: string[];
    totalProductionCount: number;
    lastProductionDate: string;
};

export type BarcodeProductionItem = {
    rawImageId: number;
    photoTypeCode: string;
    photoTypeDisplayName: string;
    sortOrder: number;
    rawFilePath: string;
    rawThumbnail: string;
    llmProcessedFilePath: string;
    llmThumbnail: string;
    status: string;
    statusNameTr: string;
    createdBy: string;
    createDate: string;
};

export type BarcodeProductionHistory = {
    barcode: string;
    items: BarcodeProductionItem[];
};

const GetBarcodeProductionSummary = async (barcode: string): Promise<BarcodeProductionSummary> => {
    const raw = (await request({
        url: ApiURL.Query.StudioQueryURLs.BatchProcessTrackingQueryURL.GetBarcodeProductionSummary,
        method: RequestType.GET,
        params: { barcode },
    })) as Raw;

    return {
        barcode: str(raw, 'barcode', 'Barcode'),
        hasPreviousProduction: Boolean(pick(raw, 'hasPreviousProduction', 'HasPreviousProduction')),
        hasInProgressProcess: Boolean(pick(raw, 'hasInProgressProcess', 'HasInProgressProcess')),
        inProgressPhotoTypeCodes: (
            (pick(raw, 'inProgressPhotoTypeCodes', 'InProgressPhotoTypeCodes') ?? []) as unknown[]
        ).map(String),
        totalProductionCount: num(raw, 'totalProductionCount', 'TotalProductionCount'),
        lastProductionDate: str(raw, 'lastProductionDate', 'LastProductionDate'),
    };
};

/** Tek istekle çok barkod; yalnızca geçmişte üretimi olan barkodlar döner. */
const GetBarcodeProductionSummaries = async (barcodes: string[]): Promise<BarcodeProductionSummary[]> => {
    if (barcodes.length === 0) {
        return [];
    }
    // ASP.NET List<string> bağlaması için tekrarlı anahtar: barcodes=a&barcodes=b
    const query = barcodes.map((barcode) => `barcodes=${encodeURIComponent(barcode)}`).join('&');
    const raw = (await request({
        url: `${ApiURL.Query.StudioQueryURLs.BatchProcessTrackingQueryURL.GetBarcodeProductionSummaries}?${query}`,
        method: RequestType.GET,
    })) as Raw;

    return list(raw, 'items', 'Items').map((entry) => ({
        barcode: str(entry, 'barcode', 'Barcode'),
        hasPreviousProduction: Boolean(pick(entry, 'hasPreviousProduction', 'HasPreviousProduction')),
        hasInProgressProcess: Boolean(pick(entry, 'hasInProgressProcess', 'HasInProgressProcess')),
        inProgressPhotoTypeCodes: (
            (pick(entry, 'inProgressPhotoTypeCodes', 'InProgressPhotoTypeCodes') ?? []) as unknown[]
        ).map(String),
        totalProductionCount: num(entry, 'totalProductionCount', 'TotalProductionCount'),
        lastProductionDate: str(entry, 'lastProductionDate', 'LastProductionDate'),
    }));
};

const GetBarcodeProductionHistory = async (barcode: string): Promise<BarcodeProductionHistory> => {
    const raw = (await request({
        url: ApiURL.Query.StudioQueryURLs.BatchProcessTrackingQueryURL.GetBarcodeProductionHistory,
        method: RequestType.GET,
        params: { barcode },
    })) as Raw;

    return {
        barcode: str(raw, 'barcode', 'Barcode'),
        items: list(raw, 'items', 'Items').map((entry) => ({
            rawImageId: num(entry, 'rawImageId', 'RawImageId'),
            photoTypeCode: str(entry, 'photoTypeCode', 'PhotoTypeCode'),
            photoTypeDisplayName: str(entry, 'photoTypeDisplayName', 'PhotoTypeDisplayName'),
            sortOrder: num(entry, 'sortOrder', 'SortOrder'),
            rawFilePath: str(entry, 'rawFilePath', 'RawFilePath'),
            rawThumbnail: str(entry, 'rawThumbnail', 'RawThumbnail'),
            llmProcessedFilePath: str(entry, 'llmProcessedFilePath', 'LlmProcessedFilePath'),
            llmThumbnail: str(entry, 'llmThumbnail', 'LlmThumbnail'),
            status: str(entry, 'status', 'Status'),
            statusNameTr: str(entry, 'statusNameTr', 'StatusNameTr'),
            createdBy: str(entry, 'createdBy', 'CreatedBy'),
            createDate: str(entry, 'createDate', 'CreateDate'),
        })),
    };
};

export const BatchProcessTrackingQuery = {
    GetItems,
    GetMetrics,
    GetDetail,
    GetHistory,
    GetBarcodeProductionSummary,
    GetBarcodeProductionSummaries,
    GetBarcodeProductionHistory,
    GetCreatedByOptions,
    GetStatusOptions,
};
